"use client"

import { useEffect, useState } from "react"
import { auth, db } from "../Firebase/firebase-config"
import { ref, get, onValue } from "firebase/database"
import { useNavigate } from "react-router-dom"
import OrderStatus from "../Components/OrderStatus"
import UserStockStatus from "../StockStatus/UserStockStatus"
import ProfileDialog from "./profile-dialog"
import { 
  Package, 
  Warehouse, 
  User, 
  LogOut, 
  ChevronLeft,
  Building2,
  UserCheck
} from "lucide-react"
import "./PartnerDashboard.css"

export default function PartnerDashboard() {
  const [partnerName, setPartnerName] = useState("")
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [partnerProfile, setPartnerProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileOpen, setProfileOpen] = useState(false)
  const [mainUserId, setMainUserId] = useState(null)
  const [activeSection, setActiveSection] = useState("orders")
  const [selectedEntity, setSelectedEntity] = useState("main")
  const [firms, setFirms] = useState([])
  const [entityName, setEntityName] = useState("Main Account")
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const navigate = useNavigate()

  // Auto collapse sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 1024)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/partner-login", { replace: true })
        return
      }

      try {
        const profileRef = ref(db, "profileDashboard")
        const profileSnap = await get(profileRef)

        if (!profileSnap.exists()) {
          alert("No profile data found!")
          setLoading(false)
          return
        }

        const profiles = profileSnap.val()
        let mainUserIdTemp = null
        let partnerData = null

        for (const userId in profiles) {
          if (profiles[userId].partners && profiles[userId].partners[user.uid]) {
            mainUserIdTemp = userId
            partnerData = profiles[userId].partners[user.uid]
            break
          }
        }

        if (!mainUserIdTemp) {
          alert("You are not assigned to any main user!")
          await auth.signOut()
          navigate("/partner-login", { replace: true })
          return
        }

        setMainUserId(mainUserIdTemp)
        setPartnerName(partnerData?.name || partnerData?.username || "Partner")
        setPartnerProfile(partnerData || null)

        const assignedFirmsIds = partnerData?.assignedFirms || []
        const profileSnapshot = await get(ref(db, `profileDashboard/${mainUserIdTemp}`))
        if (profileSnapshot.exists()) {
          const profileData = profileSnapshot.val()
          const allFirms = profileData.firms || {}

          const filteredFirms = Object.entries(allFirms)
            .filter(([firmId]) => assignedFirmsIds.includes(firmId))
            .map(([firmId, firmData]) => ({
              id: firmId,
              name: firmData.company_name || "Unnamed Firm",
              ...firmData,
            }))

          setFirms(filteredFirms)
        }

        setSelectedEntity("main")
        setEntityName("Main Account")
        setLoading(false)

        const partnerRef = ref(db, `profileDashboard/${mainUserIdTemp}/partners/${user.uid}`)
        const listener = onValue(partnerRef, (snap) => {
          if (!snap.exists()) {
            alert("Access revoked. Logging out...")
            auth.signOut()
            navigate("/partner-login")
          }
        })

        return () => listener()
      } catch (err) {
        console.error(err)
        setLoading(false)
      }
    })

    return () => unsubscribe && unsubscribe()
  }, [navigate])

  const handleLogout = async () => {
    await auth.signOut()
    localStorage.clear()
    navigate("/partner-login", { replace: true })
  }

  const handleEntityChange = (entityId, name) => {
    setSelectedEntity(entityId)
    setEntityName(name)
  }

  const currentEntityId = selectedEntity === "main" ? mainUserId : selectedEntity
 const targetIdsForOrders = selectedEntity === "main"
  ? [mainUserId]                          // Only main user orders
  : [selectedEntity]                      // Only selected firm orders

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <>
      <div className="partner-dashboard-wrapper">
        
        {/* SIDEBAR */}
       {/* Show menu button ONLY when sidebar is closed */}
{!mobileMenuOpen && (
  <button
    onClick={() => setMobileMenuOpen(true)}
    className="partner-mobile-menu-btn"
    aria-label="Open menu"
  >
    <ChevronLeft className="w-6 h-6" />
  </button>
)}


        {/* ==================== SIDEBAR (now a drawer on mobile) ==================== */}
        <aside className={`partner-sidebar ${mobileMenuOpen ? "partner-mobile-open" : ""}`}>
          
          {/* Close button inside the drawer (visible only on mobile) */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="partner-mobile-close-btn"
            aria-label="Close menu"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <div className="partner-sidebar-header">
            
            <h1 className="partner-sidebar-title">Partner Portal</h1>
           <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="partner-sidebar-toggle hidden lg:block"
            >
              <ChevronLeft className={`w-5 h-5 transition-transform ${sidebarOpen ? "rotate-180" : ""}`} />
            </button>
          </div>

          <div className="partner-account-switcher">
            <label className="partner-account-label">Active Account</label>
            <select
              value={selectedEntity}
              onChange={(e) => {
                if (e.target.value === "main") {
                  handleEntityChange("main", "Main Account")
                } else {
                  const firm = firms.find(f => f.id === e.target.value)
                  handleEntityChange(e.target.value, firm?.name || "Firm")
                }
              }}
              className="partner-account-select"
            >
              <option value="main">Main Account</option>
              {firms.map(firm => (
                <option key={firm.id} value={firm.id}>{firm.name}</option>
              ))}
            </select>
            <p className="partner-account-info">
              {selectedEntity === "main" ? <UserCheck /> : <Building2 />}
              <span>{entityName}</span>
            </p>
             <button 
                onClick={() => setProfileOpen(true)} 
                className="partner-footer-btn partner-btn-profile"
                title="View Profile"
              >
                <User className="w-4 h-4" />
                <span>Profile</span>
              </button>
              
          </div>

          <nav className="partner-sidebar-nav">
            <ul className="partner-nav-list">
              <li className="partner-nav-item">
                <button
                  onClick={() => setActiveSection("orders")}
                  className={`partner-nav-button ${activeSection === "orders" ? "active" : ""}`}
                >
                  <Package className="icon" />
                  <span>Order Status</span>
                </button>
              </li>
              <li className="partner-nav-item">
                <button
                  onClick={() => setActiveSection("stock")}
                  className={`partner-nav-button ${activeSection === "stock" ? "active" : ""}`}
                >
                  <Warehouse className="icon" />
                  <span>Stock Status</span>
                </button>
              </li>
            </ul>
              <div className="partner-sidebar-footer">
            <div className="partner-user-info">
             <button 
                onClick={handleLogout} 
                className="partner-footer-btn partner-btn-logout"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
          </nav>

         
        </aside>
{mobileMenuOpen && (
          <div
            className="partner-sidebar-overlay"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
        {/* MAIN CONTENT */}
        <div className="partner-main">
          <header className="partner-top-header partner-has-mobile-menu">
    <div className="partner-header-left">
      <h2 className="partner-header-title">
        {activeSection === "orders" ? "Order Status" : "Stock Status"}
      </h2>
      <p className="partner-header-subtitle">
        Managing <span>{entityName}</span>
      </p>
    </div>

    <div className="header-user">
      <div className="header-partner-user-info">
        <div className="header-user-name">{partnerName}</div>
        <div className="header-user-role">Partner Account</div>
      </div>
      <div className="header-avatar"  onClick={() => setProfileOpen(true)}>
        {partnerName[0]?.toUpperCase() || "P"}
      </div>
    </div>
  </header>

          <main className="content-area">
            <div className="content-card">
              {activeSection === "orders" && (
                <OrderStatus targetFirmAndMainIds={targetIdsForOrders} isPartner={true} />
              )}
              {activeSection === "stock" && (
                <UserStockStatus targetUserId={currentEntityId} />
              )}
            </div>
          </main>
        </div>
      </div>

      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        profile={partnerProfile}
        mainUserId={mainUserId}
        title="Partner Profile"
      />
    </>
  )
}